import { cn } from "@/lib/utils";
import type { TransitionClasses, TransitionEvents } from "@headlessui/react";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Transition,
} from "@headlessui/react";
import type { ComponentType, ElementType, ReactNode } from "react";
import { Fragment } from "react";

export type AccordionProps = {
  /**
   * Content displayed in the top area
   */
  title: ((open: boolean) => ReactNode) | ReactNode;
  /**
   * Content displayed when accordion is expanded
   */
  children: ReactNode;
  /**
   * Default state of the component
   */
  defaultOpen?: boolean;
  /**
   * Icon component used for the close button
   */
  IconComponent?: ComponentType<{ open: boolean }>;
  /**
   * Properites passed to the Transition component
   */
  transitionProps?: TransitionClasses &
    TransitionEvents & { appear?: boolean; show?: boolean; unmount?: boolean };
  /**
   * Classes passed to the blocks of the component
   */
  classNames?: {
    wrapper?: string;
    button?: string;
    panel?: string;
  };
  /**
   * Component or tag used for the wrapper
   */
  WrapperComponent?: ElementType;
  /**
   * Component or tag used for the close button
   */
  ButtonComponent?: ElementType;
  /**
   * Component or tag used for the content wrapper
   */
  ContentComponent?: ElementType;
};

const DefaultIconComponent = ({ open }: { open: boolean }) => (
  <span className={open ? "rotate-180 transform" : ""}>&#8744;</span>
);

export const Accordion = ({
  title,
  children,
  transitionProps = {},
  WrapperComponent = "div",
  ButtonComponent = "button",
  ContentComponent = "div",
  classNames = {},
  defaultOpen = false,
  IconComponent = DefaultIconComponent,
}: AccordionProps) => (
  <Disclosure
    as={WrapperComponent}
    defaultOpen={defaultOpen}
    className={classNames?.wrapper}
  >
    {({ open }) => (
      <>
        <DisclosureButton
          className={cn(
            "flex w-full justify-between rounded px-4 py-2 uppercase",
            classNames?.button,
          )}
          as={ButtonComponent}
        >
          {typeof title === "function" ? title(open) : title}
          {IconComponent ? <IconComponent open={open} /> : null}
        </DisclosureButton>
        <Transition
          as={Fragment}
          appear
          enter="transition-opacity duration-100 ease"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-100 ease"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          {...transitionProps}
        >
          <DisclosurePanel
            as={ContentComponent}
            {...{
              className: cn("py-2 px-4 rounded w-full", classNames?.panel),
            }}
          >
            {children}
          </DisclosurePanel>
        </Transition>
      </>
    )}
  </Disclosure>
);
